/**
 * PriorityFinding — a single ranked finding row inside the briefing card.
 *
 * Layout (per Patch 35.5.1 spec, top-to-bottom):
 *   1. Severity label + finding title
 *   2. Recommended action (verbatim from backend)
 *   3. Accept / Dismiss controls
 *   4. Expandable "Why am I seeing this?" evidence disclosure
 *
 * All copy comes from the backend. No client-side inference.
 */
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, X } from "lucide-react";

import type { AkinexFinding, AkinexSeverity } from "@/entities/akinex/api";
import { resolveActionRoute } from "@/entities/akinex/action-routing";
import { EvidenceDisclosure } from "@/entities/akinex/EvidenceDisclosure";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";

const SEVERITY_STYLE: Record<
  AkinexSeverity,
  { label: string; badgeClass: string; borderClass: string }
> = {
  critical: {
    label: "Critical",
    badgeClass: "bg-red-500/15 text-red-400 border-red-500/40",
    borderClass: "border-l-red-500",
  },
  high: {
    label: "High",
    badgeClass: "bg-amber-500/15 text-amber-400 border-amber-500/40",
    borderClass: "border-l-amber-500",
  },
  medium: {
    label: "Medium",
    badgeClass:
      "bg-yellow-500/10 text-yellow-300 border-yellow-500/30",
    borderClass: "border-l-yellow-500",
  },
  low: {
    label: "Low",
    badgeClass: "bg-white/10 text-[var(--md-ivory-0)] border-white/20",
    borderClass: "border-l-white/20",
  },
};

export interface PriorityFindingProps {
  finding: AkinexFinding;
  onAccept: (finding: AkinexFinding) => void;
  onDismiss: (finding: AkinexFinding) => void;
  disabled?: boolean;
}

export function PriorityFinding({
  finding,
  onAccept,
  onDismiss,
  disabled = false,
}: PriorityFindingProps): JSX.Element {
  const navigate = useNavigate();
  const style = SEVERITY_STYLE[finding.severity];
  const route = resolveActionRoute(finding.key);
  const testIdRoot = `akinex-finding-${finding.key}`;

  const handleAccept = () => {
    // Dispatch the decision to the mutation. Toast + briefing refetch
    // are wired at the mutation-observer level (see useAkinexDecision)
    // so they survive the navigation-driven unmount below. On POST
    // failure the founder will see an error toast on the destination
    // page — navigation is intentionally fire-and-forget.
    onAccept(finding);
    if (route) {
      navigate(route.path);
    }
  };

  return (
    <li
      data-testid={testIdRoot}
      data-severity={finding.severity}
      className={`rounded-xl border border-white/5 border-l-4 ${style.borderClass} bg-black/10 p-4`}
    >
      <div className="flex flex-wrap items-start gap-2">
        <Badge
          variant="outline"
          data-testid={`${testIdRoot}-severity`}
          className={`shrink-0 border ${style.badgeClass}`}
        >
          {style.label}
        </Badge>
        <h3
          data-testid={`${testIdRoot}-title`}
          className="text-sm font-semibold text-[var(--md-ivory-0)] leading-snug"
        >
          {finding.title}
        </h3>
      </div>

      <p
        data-testid={`${testIdRoot}-action`}
        className="mt-2 text-sm text-[var(--md-fog)]"
      >
        {finding.recommended_action}
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={disabled}
          onClick={handleAccept}
          data-testid={`${testIdRoot}-accept`}
        >
          <Check className="h-3.5 w-3.5 mr-1.5" aria-hidden />
          {route ? route.label : "Accept"}
          {route ? (
            <ArrowRight className="h-3.5 w-3.5 ml-1.5" aria-hidden />
          ) : null}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={disabled}
          onClick={() => onDismiss(finding)}
          data-testid={`${testIdRoot}-dismiss`}
        >
          <X className="h-3.5 w-3.5 mr-1.5" aria-hidden />
          Dismiss
        </Button>
        {!route ? (
          <span
            data-testid={`${testIdRoot}-unsupported-action`}
            className="text-[11px] text-[var(--md-fog)]"
          >
            No in-app destination — decision will be recorded on the timeline.
          </span>
        ) : null}
      </div>

      <EvidenceDisclosure
        findingKey={finding.key}
        evidence={finding.evidence}
        confidence={finding.confidence}
      />
    </li>
  );
}
